/**
 * Structure de navigation du détail d'une campagne : des onglets par TEST, et
 * dans chaque test ses sections.
 *
 * La hiérarchie suit celle des données : un référentiel, des référents et un
 * matching appartiennent à un test, pas l'inverse. L'ancienne page rangeait par
 * fonction (Statistiques / Référentiel / Matching) sous un sélecteur de test :
 * changer de test faisait apparaître et disparaître des onglets.
 *
 * Ce qui est ici est STATIQUE — la liste des onglets ne dépend que de la
 * configuration de la campagne, celle des sections que du test. Ce qui dépend
 * des données (un référentiel est-il posé ?) se décide dans la vue.
 */
import type { TestCampagne } from "@repo/results";

/** Onglets transverses aux tests. */
export type OngletGeneral = "parcours" | "synthese";
export type OngletCampagne = OngletGeneral | TestCampagne;
export type SectionTest = "resultats" | "matching" | "referentiel" | "referents";

export const LIBELLE_TEST: Record<TestCampagne, string> = {
  inbasket: "In-Basket",
  entstruct: "FairView",
  pami: "PAMI",
  fitculturel: "PC Fit",
  copsoq3: "COPSOQ",
};

export const LIBELLE_SECTION: Record<SectionTest, string> = {
  resultats: "Résultats",
  matching: "Matching",
  referentiel: "Référentiel",
  referents: "Référents",
};

/**
 * Sections qu'un test PEUT porter, dans l'ordre d'affichage. La vue en retire
 * celles qui n'ont encore rien à montrer (Référents sans référentiel posé).
 *
 * L'In-Basket ne produit pas de score global : ni matching ni référentiel.
 * L'entretien structuré se compare au barème de son template, pas à un
 * référentiel de référents : Matching oui, Référentiel / Référents non.
 * Le COPSOQ III est un diagnostic, pas une comparaison à un poste : Résultats
 * seuls.
 */
export const SECTIONS_DU_TEST: Record<TestCampagne, readonly SectionTest[]> = {
  inbasket: ["resultats"],
  entstruct: ["resultats", "matching"],
  pami: ["resultats", "matching", "referentiel", "referents"],
  fitculturel: ["resultats", "matching", "referentiel", "referents"],
  copsoq3: ["resultats"],
};

/**
 * Cible de référentiel adossée à un test : les attentes du poste pour le PAMI,
 * la culture d'entreprise pour le fit culturel.
 */
export const CIBLE_DU_TEST: Partial<
  Record<TestCampagne, "pami" | "pc-fit">
> = {
  pami: "pami",
  fitculturel: "pc-fit",
};

const TESTS: readonly TestCampagne[] = [
  "inbasket",
  "entstruct",
  "pami",
  "fitculturel",
  "copsoq3",
];

export function estTest(onglet: string): onglet is TestCampagne {
  return (TESTS as readonly string[]).includes(onglet);
}

/**
 * Tests que la campagne fait passer, dans l'ordre des onglets.
 *
 * La passation candidat fit-culturel porte le template `fit-culturel` — à ne
 * pas confondre avec `referentiel`, conteneur interne des contributions de
 * référents, qui n'est jamais un test de candidat.
 */
export function testsDeLaCampagne(
  config: readonly { typeEval: string; template?: string }[],
): TestCampagne[] {
  const a = (predicat: (e: { typeEval: string; template?: string }) => boolean) =>
    config.some(predicat);
  const tests: TestCampagne[] = [];
  if (a((e) => e.typeEval === "inbasket")) tests.push("inbasket");
  if (a((e) => e.typeEval === "fairview")) tests.push("entstruct");
  if (a((e) => e.typeEval === "form" && e.template === "pami")) tests.push("pami");
  if (a((e) => e.typeEval === "form" && e.template === "pc-fit"))
    tests.push("fitculturel");
  if (a((e) => e.typeEval === "form" && e.template === "copsoq3"))
    tests.push("copsoq3");
  return tests;
}

/** Tests sans score global : absents de la Synthèse. */
export const SANS_SCORE_GLOBAL: readonly TestCampagne[] = ["inbasket", "copsoq3"];

/**
 * Tests qui produisent un score global — ceux que la Synthèse met côte à côte.
 * L'In-Basket n'en a pas, le COPSOQ III non plus (six domaines, aucun total).
 */
export function testsSynthese(tests: readonly TestCampagne[]): TestCampagne[] {
  return tests.filter((t) => !SANS_SCORE_GLOBAL.includes(t));
}

/**
 * La Synthèse compare des tests entre eux : avec un seul test noté, elle ne
 * ferait que répéter ses Résultats.
 */
export function aSynthese(tests: readonly TestCampagne[]): boolean {
  return testsSynthese(tests).length >= 2;
}
